import { html, LitElement, TemplateResult } from 'lit';
import { styles } from '../view/styles';
import { GzTabWebComponent } from '../../gzTab';
import { GzTabChangeEvent } from '../events/GzTabChangeEvent';
import { property } from 'lit/decorators.js';
import { NavigationHelper } from '@nirvana/shared/navigation';

export class GzTabGroupWebComponent extends LitElement {
  private tabs: GzTabWebComponent[] = [];
  private selectedTabIndex = -1;

  @property({ type: Boolean, reflect: true })
  fillSpace = false;

  @property({ type: Boolean, reflect: true })
  noWrap = false;

  public static get styles() {
    return styles;
  }

  public selectTab(tabIndex: number, suppressEvent: boolean = false) {
    if (tabIndex === this.selectedTabIndex || tabIndex >= this.tabs.length || tabIndex < 0) {
      return;
    }

    const previousTab = this.selectedTabIndex;
    this.selectedTabIndex = tabIndex;

    for (let i = 0; i < this.tabs.length; i++) {
      this.tabs[i].selected = tabIndex === i;
    }

    if(!suppressEvent) {
      this.dispatchEvent(new GzTabChangeEvent(this, previousTab, this.selectedTabIndex));
    }

    this.requestUpdate();
  }

  protected firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);

    this.queryHtmlForTabs();

    const hasPreSelectedTab = this.tabs.length && this.tabs.find(tab => tab.selected);

    if (!hasPreSelectedTab) {
      this.selectTab(0);
    } else {
      this.requestUpdate();
    }
  }

  private queryHtmlForTabs() {
    const slot = this.shadowRoot?.querySelector('slot');
    this.tabs = slot
      ? (Array.from(slot.assignedNodes()).filter(child => child instanceof GzTabWebComponent) as GzTabWebComponent[])
      : [];
  }

  private renderHeaderButtons(): TemplateResult[] {
    const headerButtons: TemplateResult[] = [];
    this.tabs.forEach((tab: GzTabWebComponent, tabIndex) => {
      if (!tab.hidden) {
        headerButtons.push(html`
          <button
            class="header-button"
            @click="${() => tab.route ? NavigationHelper.navigateTo(tab.route) : this.selectTab(tabIndex)}"
            class="${tab.selected ? 'selected' : ''}"
            ?disabled="${tab.disabled}"
          >
            <gz-tooltip tooltipText="${tab.label}" isEllipsis>
              <div class="header-text" part="label-text">${tab.label}</div>
            </gz-tooltip>
          </button>
        `);
      }
    });

    return headerButtons;
  }

  public render() {
    return html`
      <div class="gz-tab-group" part="tab-group">
        <section class="gz-tab-group-header">${this.renderHeaderButtons()}</section>
        <div class="tab-slot-wrapper">
          <slot @slotchange="${this.handleSlotChange}"></slot>
        </div>
      </div>
    `;
  }

  private handleSlotChange() {
    this.queryHtmlForTabs();
  }

  public disableTabs(tabIndexes: Array<number>) {
    this.setTabsDisabledAttribute(tabIndexes, true);
  }

  public enableTabs(tabIndexes: Array<number>) {
    this.setTabsDisabledAttribute(tabIndexes, false);
  }

  public hideTabs(tabIndexes: Array<number>) {
    this.setTabsHiddenAttribute(tabIndexes, true);
  }

  public showTabs(tabIndexes: Array<number>) {
    this.setTabsHiddenAttribute(tabIndexes, false);
  }

  public setDisabledStateForUnselectedTabs(isDisabled: boolean) {
    const tabsToChange: Array<number> = [];
    for (let i = 0; i < this.tabs.length; i++) {
      tabsToChange.push(i);
    }

    if (tabsToChange.length > 0) {
      this.setTabsDisabledAttribute(tabsToChange, isDisabled);
    }
  }

  private setTabsDisabledAttribute(tabIndexes: Array<number>, isDisabled: boolean) {
    let shouldRequestUpdate = false;
    for (let i = 0; i < tabIndexes.length; i++) {
      // Forbid disabled attribute for selected tab
      // Check if all given indexes are defined
      if (this.tabs[tabIndexes[i]] && tabIndexes[i] !== this.selectedTabIndex) {
        this.tabs[tabIndexes[i]].disabled = isDisabled;
        shouldRequestUpdate = true;
      }
    }

    // If there are valid attribute tab changes, request update
    if (shouldRequestUpdate) {
      this.requestUpdate();
    }
  }

  private setTabsHiddenAttribute(tabIndexes: Array<number>, isHidden: boolean) {
    let shouldRequestUpdate = false;
    for (let i = 0; i < tabIndexes.length; i++) {
      // Forbid hidden attribute for selected tab
      // Check if all given indexes are defined
      if (this.tabs[tabIndexes[i]] && tabIndexes[i] !== this.selectedTabIndex) {
        this.tabs[tabIndexes[i]].hidden = isHidden;
        shouldRequestUpdate = true;
      }
    }

    // If there are valid attribute tab changes, request update
    if (shouldRequestUpdate) {
      this.requestUpdate();
    }
  }
}
